import differenceInDays from 'date-fns/difference_in_days';
import differenceInYears from 'date-fns/difference_in_years';
import get from 'lodash/get';
import { isEmpty } from 'ravenjs/utils/lodash';

import Acl from 'modules/acl';
import { isGenesisInternalUser, isParentPartnerAdminUser } from 'utils/user';
import { addSchemaValue } from 'ravenjs/utils/schema';
import _cloneDeep from 'lodash/cloneDeep';
import findIndex from 'lodash/findIndex';
import last from 'lodash/last';

import { isValidRangeBetweenYears } from 'utils/date';

import {
    CLIENT_SOLUTION_REPORT,
    CLIENT_USAGE_ADMIN_ROLES,
    GENERATED_REPORT_MESSAGE,
    GENERATED_REPORT_MESSAGE_CSV,
    GENERATED_REPORT_MESSAGE_PDF,
    CVR_BREAKPOINTS_COMPANY_SIZE,
} from 'constants/reports';
import { ROLE_TYPE } from 'constants/roles';
import { REPORTS_NAV_ITEMS } from 'constants/navigation';

/**
 * Get subnavigation links.
 *
 * @method getFormattedLinks
 * @param  {Object} items
 * @return {Object}
 */
export const getFormattedLinks = items => {
    const links = items.map(({ title, to, permission }) => {
        if (Acl.check(permission)) {
            return {
                label: title,
                jumpTo: to,
            };
        }
        return null;
    });
    return links.filter(el => el != null);
};

export const isClientSolutionReportDisabled = (
    userCompany,
    userRole,
    isParentPartnerAdminValue
) => {
    const hasLicense = Boolean(userCompany?.licenses?.length > 0);
    const isLoggedInIntuitPartner =
        userCompany?.displayName === CLIENT_SOLUTION_REPORT.INTUIT_PARTNER_NAME;
    const isLoggedInUserRoleInternal = isGenesisInternalUser(userRole);
    const isParentPartnerAdmin = isParentPartnerAdminUser({
        isParentPartnerAdmin: isParentPartnerAdminValue,
    });
    const isPrimaryCompany = userCompany?.isPrimaryCompany;

    if (isParentPartnerAdmin && isPrimaryCompany === false) {
        return true;
    }

    if (isLoggedInUserRoleInternal || isParentPartnerAdmin) {
        return false;
    }

    return !hasLicense || isLoggedInIntuitPartner;
};

export const getEsignatureReportingSchema = options => {
    const { reportSchema, maxTotalSelected, resetAll, forceSelectAll } = options;

    const newReportSchema = _cloneDeep(reportSchema);

    addSchemaValue(
        newReportSchema,
        'uiReportingSchema.esignatureReporting.selectHandbook.ui:options.maxTotalSelected',
        maxTotalSelected
    );

    if (forceSelectAll) {
        addSchemaValue(
            newReportSchema,
            'uiReportingSchema.esignatureReporting.selectHandbook.ui:options.forceSelectAll',
            forceSelectAll
        );
    } else {
        addSchemaValue(
            newReportSchema,
            'uiReportingSchema.esignatureReporting.selectHandbook.ui:options.resetAll',
            resetAll
        );
    }

    addSchemaValue(
        newReportSchema,
        'uiReportingSchema.esignatureReporting.selectHandbook.ui:options.defaultOptions',
        []
    );

    return newReportSchema;
};

export const handleFormChangeUtility = async ({ formData, state, fetchTotalDocument }) => {
    const {
        isMineralRole,
        reportSchema,
        previousCompany,
        isClientRole,
        isParentPartnerAdmin,
    } = state;
    const currentPartner = get(formData, 'esignatureReporting.selectPartner.value', '');
    const currentCompany = get(formData, 'esignatureReporting.selectCompany.value', '');

    if (isClientRole) {
        return { ...state };
    }

    if (!currentPartner && isMineralRole && !isParentPartnerAdmin) {
        const updateFormSchema = _cloneDeep(reportSchema);
        formData.esignatureReporting.selectCompany = null;
        formData.esignatureReporting.selectHandbook = null;

        addSchemaValue(
            updateFormSchema,
            'uiReportingSchema.esignatureReporting.selectCompany.ui:options.inputValue',
            null
        );
        addSchemaValue(
            updateFormSchema,
            'uiReportingSchema.esignatureReporting.selectCompany.ui:options.value',
            null
        );

        return {
            formData: {},
            previousCompany: '',
            messageSuccess: '',
            showSuccess: false,
            reportSchema: updateFormSchema,
        };
    }

    if (!currentCompany) {
        const updateFormSchema = getEsignatureReportingSchema({
            reportSchema,
            maxTotalSelected: 0,
            resetAll: true,
        });

        return {
            formData: {},
            previousCompany: '',
            messageSuccess: '',
            showSuccess: false,
            excludeHandbooks: [],
            includeHandbook: [],
            reportSchema: updateFormSchema,
            totalDocument: 0,
        };
    } else {
        if (currentCompany !== previousCompany) {
            let updateFormSchema = _cloneDeep(reportSchema);
            addSchemaValue(
                updateFormSchema,
                'uiReportingSchema.esignatureReporting.selectHandbook.ui:options.resetAll',
                true
            );

            const totalDocuments = await fetchTotalDocument(currentCompany, true);

            updateFormSchema = getEsignatureReportingSchema({
                reportSchema: updateFormSchema,
                maxTotalSelected: totalDocuments,
            });

            return {
                previousCompany: currentCompany,
                reportSchema: updateFormSchema,
            };
        }

        return { previousCompany: currentCompany };
    }
};

export const isAdminRoleAllowToClientUsageReport = roleName => {
    return CLIENT_USAGE_ADMIN_ROLES.includes(roleName);
};

export const getExportMessage = (isCsv, isPdf) => {
    if (isPdf && isCsv) {
        return GENERATED_REPORT_MESSAGE;
    }
    if (isPdf) {
        return GENERATED_REPORT_MESSAGE_PDF;
    }
    if (isCsv) {
        return GENERATED_REPORT_MESSAGE_CSV;
    }
    return '';
};

export const isCvrRunButtonDisabled = formData => {
    const report = get(formData, 'report', {});
    const { from, until, selectedClient, selectedCompanySize } = report;

    return (
        !until ||
        !from ||
        differenceInYears(until, from) < 0 ||
        differenceInDays(until, from) < 0 ||
        isEmpty(selectedClient) ||
        isEmpty(selectedCompanySize)
    );
};

export const getAdminRoleStateToClientUsage = ({ currentUserRole, isParentPartnerAdmin }) => {
    const until = new Date();
    const from = new Date();
    from.setFullYear(from.getFullYear() - 1);
    const isAdminRole =
        isParentPartnerAdminUser({ isParentPartnerAdmin }) ||
        isAdminRoleAllowToClientUsageReport(currentUserRole);

    const disableRunButton = !isValidRangeBetweenYears(until, from, 2) || isAdminRole;

    return {
        disableRunButton,
        isAdminRole,
        clientUsageReport: {},
        formData: {
            report: {
                from: until,
                until: from,
            },
        },
    };
};

export const getInitialReportStatePvr = (
    { companyInfo, isParentPartnerAdminValue, userRole },
    schema
) => {
    const previousDate = new Date();
    previousDate.setFullYear(previousDate.getFullYear() - 1);
    const { companyType } = companyInfo;
    const isParentPartnerAdmin = isParentPartnerAdminUser({
        isParentPartnerAdmin: isParentPartnerAdminValue,
    });

    let isLoggedInPartnerUser =
        companyType === ROLE_TYPE.PARTNER && !isGenesisInternalUser(userRole);

    if (isParentPartnerAdmin) {
        isLoggedInPartnerUser = false;
    }

    const clonedSchema = schema ? formSchemaPvr(isLoggedInPartnerUser, schema) : false;

    return {
        currentParamsReport: {},
        defaultJumpTo: { label: REPORTS_NAV_ITEMS[3].title, value: REPORTS_NAV_ITEMS[3].to },
        excludeClients: [],
        formData: {
            generateReport: {
                endDate: new Date(),
                startDate: previousDate,
            },
        },
        formDataCalculationValues: {},
        generatedReport: {},
        includeClients: [],
        isInactiveClientsIncluded: false,
        isLoggedInPartnerUser,
        pvrSchema: clonedSchema,
        selectAllClients: true,
        downloadCheckboxes: {
            csv: false,
            pdf: false,
        },
    };
};

export const formSchemaPvr = (isPartnerUser = false, schema) => {
    const clonedSchema = _cloneDeep(schema);

    if (isPartnerUser) {
        addSchemaValue(
            clonedSchema,
            'schema.properties.generateReport.properties.selectedPartner',
            {}
        );
        addSchemaValue(
            clonedSchema,
            'schema.properties.generateReport.properties.includeInactivePartners',
            {}
        );
        addSchemaValue(clonedSchema, 'schema.properties.generateReport.required', [
            'startDate',
            'endDate',
        ]);
    }

    return clonedSchema;
};

export function getCompanySizeLabel(companySize) {
    if (!companySize || companySize < 0) return '';

    const breakpoints = CVR_BREAKPOINTS_COMPANY_SIZE.NUMERIC;
    const labels = CVR_BREAKPOINTS_COMPANY_SIZE.LABELS;

    const index = findIndex(breakpoints, bp => companySize <= bp);

    return index === -1 ? last(labels) : labels[index];
}
